import {
  Box,
  Text,
  VStack,
  HStack,
  Progress,
  Card,
  CardBody,
  Heading,
} from "@chakra-ui/react";
import { useTransactions } from "../context/TransactionsContext";
import { useExpenseLimits } from "../context/ExpenseLimitContext";
import { useCurrentMonth } from "../hooks/useCurrentMonth";
import { formatEuro, formatMonthYear } from "../utils/helperFunctions";
import { useCustomCategories } from "../context/CustomCatgoriesContext";
import { useState } from "react";
import { Button } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

export const ExpenseLimitsProgress = () => {
  const { transactions } = useTransactions();
  const { expenseLimits } = useExpenseLimits();
  const { categories: customCategories } = useCustomCategories();
  const currentMonth = useCurrentMonth();
  const [isOpen, setIsOpen] = useState(false);

  const monthBudgets = expenseLimits[currentMonth] || [];

  const expensesThisMonth = transactions.filter(
    (t) => t.type === "expense" && t.date.startsWith(currentMonth)
  );

  const getSpentForCategory = (category: string) =>
    expensesThisMonth
      .filter((t) => t.category === category)
      .reduce((sum, t) => sum + t.amount, 0);

  const validCategoryValues = customCategories.map((c) => c.value);

  const filteredBudgets = monthBudgets.filter((b) =>
    validCategoryValues.includes(b.category)
  );

  if (filteredBudgets.length === 0) return null;

  return (
    <Card.Root mt={6} shadow="sm">
      <CardBody p={0}>
        <HStack justify="space-between" p={3}>
          <Heading size="md">
            Ausgabenlimits - {formatMonthYear(currentMonth)}
          </Heading>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Weniger anzeigen" : "Sparziele anzeigen"}
          >
            {/* @ts-ignore */}
            {isOpen ? <IoChevronUp /> : <IoChevronDown />}
          </Button>
        </HStack>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              style={{ overflow: "hidden" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <VStack spaceY={5} align="stretch" p={5}>
                {filteredBudgets.map((b) => {
                  const spent = getSpentForCategory(b.category);
                  const percentage = Math.min((spent / b.amount) * 100, 100);
                  const overBudget = spent > b.amount;

                  return (
                    <Box key={b.category}>
                      <HStack justify="space-between" mb={1}>
                        <Text fontWeight="medium">{b.category}</Text>
                        <Text
                          fontSize="sm"
                          color={overBudget ? "red.500" : "gray.600"}
                        >
                          {formatEuro(spent)} / {formatEuro(b.amount)}
                        </Text>
                      </HStack>
                      <Progress.Root
                        value={percentage}
                        size="md"
                        borderRadius="md"
                        animated={true}
                      >
                        <Progress.Track>
                          <Progress.Range
                            style={{
                              backgroundColor: overBudget ? "red" : "black",
                            }}
                          />
                        </Progress.Track>
                      </Progress.Root>
                    </Box>
                  );
                })}
              </VStack>
            </motion.div>
          )}
        </AnimatePresence>
      </CardBody>
    </Card.Root>
  );
};
